import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChartLine,
  Database,
  FileUp,
  FolderKanban,
  LifeBuoy,
  Percent,
  Plus,
  Target,
  UserCheck,
} from "lucide-react";
import DashboardHeader from "../../components/dashboard/DashboardHeader";
import KpiCard from "../../components/dashboard/KpiCard";
import NoticeBoardCard from "../../components/notices/NoticeBoardCard";
import ProjectsAttentionTable from "../../components/dashboard/ProjectsAttentionTable";
import ProjectMixCard from "../../components/dashboard/ProjectMixCard";
import PerformanceSnapshotCard from "../../components/dashboard/PerformanceSnapshotCard";
import ActivityTrendCard from "../../components/dashboard/ActivityTrendCard";
import LoggingStatusCard from "../../components/dashboard/LoggingStatusCard";
import RecentLogsCard from "../../components/dashboard/RecentLogsCard";
import { KPI_DEFINITIONS as ACTIVITY_METRICS } from "../../components/project-management/ActivityLogTab";
import { fetchManagerDashboard, MAX_LOG_PROJECTS, TREND_DAYS } from "../../controllers/dashboardController";
import { formatRate } from "../performance/performanceUtils";

// `openCreate` state tells the Project Management page to open its create modal.
const QUICK_ACTIONS = [
  { label: "New Project", icon: Plus, to: "/admin/project-management", state: { openCreate: true }, primary: true },
  { label: "Records", icon: Database, to: "/admin/database-mangement" },
  { label: "Import Data", icon: FileUp, to: "/admin/import-data" },
  { label: "Performance", icon: ChartLine, to: "/admin/performance-dashboard" },
];

// Leaderboard statuses that signal someone may need support.
const SUPPORT_STATUSES = ["Underdelivering", "Unacceptable Output"];

const fmtN = (n) => Number(n || 0).toLocaleString();
const UNAVAILABLE = { value: "—", sub: "Couldn't load" };

const logsFootnote = (activity) => {
  const notes = [];
  if (activity?.skippedProjects) notes.push(`Logs from the first ${MAX_LOG_PROJECTS} active projects only.`);
  if (activity?.failedProjects) notes.push(`Logs for ${activity.failedProjects} project(s) couldn't be loaded.`);
  return notes.join(" ") || undefined;
};

const buildCards = ({ projects, records, performance, activity }) => {
  const p = projects?.data;
  const perf = performance?.data;
  const a = activity?.data;
  const needSupport = perf ? SUPPORT_STATUSES.reduce((sum, s) => sum + (perf.byStatus[s] || 0), 0) : 0;

  return [
    {
      key: "projects",
      label: "Active Projects",
      icon: FolderKanban,
      color: "blue",
      to: "/admin/project-management",
      ...(p
        ? {
            value: fmtN(p.active),
            sub: `${fmtN(p.overdue)} overdue`,
            badge: p.overdue > 0 ? "Attention" : undefined,
          }
        : UNAVAILABLE),
    },
    {
      key: "recruitment",
      label: "Recruitment Progress",
      icon: Target,
      color: "green",
      to: "/admin/project-management",
      ...(p
        ? {
            value: p.targets ? `${Math.round((p.recruited / p.targets) * 100)}%` : "—",
            sub: `${fmtN(p.recruited)} of ${fmtN(p.targets)} recruited`,
          }
        : UNAVAILABLE),
    },
    {
      key: "conversion",
      label: "Team Conversion",
      icon: Percent,
      color: "purple",
      to: "/admin/performance-dashboard",
      ...(perf
        ? { value: formatRate(perf.teamConversion), sub: `${fmtN(perf.totalOutput)} screener responses` }
        : UNAVAILABLE),
    },
    {
      key: "support",
      label: "Needs Support",
      icon: LifeBuoy,
      color: "red",
      to: "/admin/performance-dashboard",
      ...(perf
        ? {
            value: fmtN(needSupport),
            sub: `of ${fmtN(perf.total)} ranked`,
            badge: needSupport > 0 ? "Review" : undefined,
          }
        : UNAVAILABLE),
    },
    {
      key: "logged",
      label: "Logged Today",
      icon: UserCheck,
      color: "orange",
      ...(a
        ? {
            value: `${fmtN(a.loggedTodayCount)}/${fmtN(a.assigneeCount)}`,
            sub: "assignees",
            badge:
              a.assigneeCount > a.loggedTodayCount
                ? `${fmtN(a.assigneeCount - a.loggedTodayCount)} pending`
                : undefined,
          }
        : UNAVAILABLE),
    },
    {
      key: "records",
      label: "Records",
      icon: Database,
      color: "dark",
      to: "/admin/database-mangement",
      ...(records?.data
        ? {
            value: fmtN(records.data.total),
            sub: `Warm ${fmtN(records.data.warm)} · Cold ${fmtN(records.data.cold)}`,
          }
        : UNAVAILABLE),
    },
  ];
};

const LOADING = { projects: {}, records: {}, performance: {}, activity: {} };

/** Team dashboard for the "manager" role. */
function ManagerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchManagerDashboard().then((result) => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLoading = data === null;
  const cards = buildCards(data || LOADING);
  const projects = data?.projects?.data;
  const projectsFailed = Boolean(data?.projects?.error);
  const activity = data?.activity;
  const activityFailed = Boolean(activity?.error);

  return (
    <div className="w-full max-w-full space-y-6">
      <DashboardHeader
        subtitle="How your projects and team are tracking today."
        actions={QUICK_ACTIONS}
      />

      <section aria-label="Key metrics" className="grid grid-cols-2 gap-4 md:grid-cols-3 2xl:grid-cols-6">
        {cards.map(({ key, to, ...card }) => (
          <KpiCard
            key={key}
            {...card}
            loading={isLoading}
            onClick={to ? () => navigate(to) : undefined}
          />
        ))}
      </section>

      <NoticeBoardCard />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-8">
          <ProjectsAttentionTable projects={projects?.attention} loading={isLoading} error={projectsFailed} />
        </div>
        <div className="min-w-0 xl:col-span-4">
          <ProjectMixCard
            byPriority={projects?.byPriority}
            byType={projects?.byType}
            total={projects?.active}
            loading={isLoading}
            error={projectsFailed}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-7">
          <PerformanceSnapshotCard
            data={data?.performance?.data}
            loading={isLoading}
            error={Boolean(data?.performance?.error)}
          />
        </div>
        <div className="min-w-0 xl:col-span-5">
          <ActivityTrendCard
            title="Team Activity"
            description={`Activity logs submitted per day, last ${TREND_DAYS} days.`}
            unit="logs"
            data={activity?.data}
            loading={isLoading}
            error={activityFailed}
            emptyText={`No activity logged on active projects in the last ${TREND_DAYS} days.`}
            footnote={logsFootnote(activity?.data)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-5">
          <LoggingStatusCard data={activity?.data} loading={isLoading} error={activityFailed} />
        </div>
        <div className="min-w-0 xl:col-span-7">
          <RecentLogsCard
            title="Recent Team Logs"
            description="Latest activity entries across active projects."
            logs={activity?.data?.recent}
            metrics={ACTIVITY_METRICS}
            showAuthor
            loading={isLoading}
            error={activityFailed}
            emptyText={`No activity logged in the last ${TREND_DAYS} days.`}
          />
        </div>
      </div>
    </div>
  );
}

export default ManagerDashboard;
